/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getExpensesPendingId } from '@/services/Expenses/request';
import { getPopups } from '@/services/Popups/request';

import { PopupsProps } from '@/context/CustomPopupsContext/types';
import { useUser } from '@/context/UserContext';

import { useCustomPopups } from '@/hooks/useCustomPopups/useCustomPopups';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

import PopupTemplate1 from '@/components/CustomPopups/PopupTemplate1';
import PopupTemplate2 from '@/components/CustomPopups/PopupTemplate2';
import PopupTemplate3 from '@/components/CustomPopups/PopupTemplate3';

import { Backdrop, Container, Content } from './styles';

const INTERVAL_TIME_IN_MILLISECONDS = 10 * 60 * 1000;
const TIMEOUT_TIME_IN_MILLISECONDS = 5000;

const CustomPopupsLayout = () => {
  const { featureFlags } = useFeatureFlags();

  const navigate = useNavigate();
  const { isLoggedIn, userInformation } = useUser();
  const roles = userInformation?.roles || [];

  const { popupsList, setPopupsList } = useCustomPopups();
  const [isLogged, setIsLogged] = useState(false);

  const fetchPopups = async () => {
    try {
      const response = await getPopups();
      setPopupsList(response);
    } catch {
      setPopupsList([]);
    }
  };

  console.log(featureFlags.ff_sapron_popup_expense_pending);

  const checkPendingExpenses = async () => {
    if (
      !userInformation?.user_id ||
      !roles.includes('Host') ||
      !featureFlags.ff_sapron_popup_expense_pending
    )
      return;

    try {
      const pendingExpenses = await getExpensesPendingId(Number(userInformation.user_id));

      if (pendingExpenses && pendingExpenses.length > 0) {
        const expensePopup = {
          id: -1,
          headline: '⚠️ Você possui reembolsos pendentes!',
          content:
            'Identificamos que você possui solicitações de reembolso pendentes. Acesse a tela de reembolsos lançados e edite as solicitações conforme indicado.',
          confirm_button_text: 'Ir para reembolsos',
          type: 'PendingExpense' as const,
          popup_is_open: true,
          onConfirm: () => {
            navigate('/reembolso');
          },
        };

        setPopupsList((prevPopups: any) => {
          const hasExpensePopup = prevPopups.some((popup: any) => popup.id === -1);
          if (!hasExpensePopup) {
            return [...prevPopups, expensePopup];
          }
          return prevPopups;
        });
      }
    } catch (error) {
      // Erro ao verificar reembolsos pendentes
    }
  };

  useEffect(() => {
    let timerInterval: NodeJS.Timeout;
    let timeoutDelay: NodeJS.Timeout;

    if (isLoggedIn) {
      fetchPopups(); // GET inicial na api /popups/
      checkPendingExpenses(); // Verifica reembolsos pendentes

      // GET na api de /popups/ a cada x intervalos de tempo em milissegundos
      timerInterval = setInterval(() => {
        fetchPopups();
      }, INTERVAL_TIME_IN_MILLISECONDS);

      // Tempo extra de espera para que o popup seja exibido em tela
      timeoutDelay = setTimeout(() => {
        setIsLogged(true);
      }, TIMEOUT_TIME_IN_MILLISECONDS);
    } else {
      setIsLogged(false);
    }

    return () => {
      clearInterval(timerInterval);
      clearTimeout(timeoutDelay);
    };
  }, [isLoggedIn, featureFlags.ff_sapron_popup_expense_pending]);

  const popupIsOpen = useMemo(
    () => popupsList.some((item) => item.popup_is_open === true),
    [popupsList],
  );

  if (!popupIsOpen) return null;

  interface IPopups {
    popup: PopupsProps;
  }

  const Popups = ({ popup }: IPopups) => (
    <Container key={popup.id} type={popup.type}>
      <Content type={popup.type}>
        {popup.type === 'Default' && (
          <PopupTemplate1
            id={popup.id}
            headline={popup.headline}
            content={popup.content}
            confirmButtonText={popup.confirm_button_text}
            consentRequired={popup?.consent_required || false}
            consentText={popup?.consent_text || ''}
            consentDescription={popup?.consent_description || ''}
          />
        )}
        {popup.type === 'InfoWithImage' && (
          <PopupTemplate2
            id={popup.id}
            headline={popup.headline}
            content={popup.content}
            confirmButtonText={popup.confirm_button_text}
            imageUrl={popup?.image_url || '#'}
          />
        )}
        {popup.type === 'PendingExpense' && (
          <PopupTemplate3
            id={popup.id}
            headline={popup.headline}
            content={popup.content}
            confirmButtonText={popup.confirm_button_text}
            onConfirm={popup.onConfirm}
          />
        )}
      </Content>
    </Container>
  );

  return (
    <Backdrop isLogged={isLogged}>
      {popupsList.map((popup) => popup.popup_is_open && <Popups key={popup.id} popup={popup} />)}
    </Backdrop>
  );
};

export default CustomPopupsLayout;
